import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import { saveAdminSsoPolicy, saveSsoRequiredLevels, type AuthenticationPage } from '../../api/settingsAuthentication'
import { toast } from '../../ui/notify'
import { AccessLevelLegend } from '../users/AccessLevelLegend'
import { ACCESS_LEVEL_INFO } from '../users/accessLevels'

/** Nivelurile care se pot bifa - aceleasi ca `AdminSsoPolicy::LEVELS` din v4/api. */
export const SSO_LEVELS: readonly number[] = [1, 2, 3, 6]

/**
 * Nivelurile vechi urmeaza bifa nivelului cu care seamana
 * (`AdminSsoPolicy::FOLLOWS`): Super Admin (legacy) pe Super Admin, GPS
 * Dispatcher (legacy) pe Admin.
 */
const ALSO: Readonly<Record<number, string>> = {
  1: 'Also applies to GPS Dispatcher (legacy).',
  2: 'Also applies to Super Admin (legacy).',
}

/**
 * Ce se bifeaza la trecerea de pe regula veche. Regula veche aprinsa prindea
 * sigur nivelurile Service Provider (3) si Super Admin vechi (4, care urmeaza
 * Super Admin); restul veneau din rol si drepturi, care pe nivel nu mai
 * conteaza - deci nu se ghicesc. Stinsa = nimic bifat.
 */
export function legacyStartLevels(adminSsoRequired: boolean): number[] {
  return adminSsoRequired ? [2, 3] : []
}

/**
 * "Sign in only with single sign-on for these access levels" (19 octombrie
 * 2026), in locul comutatorului "Administrators must sign in with single
 * sign-on".
 *
 * O politica de dinainte (`ssoRequiredLevels` null) ramane pe regula veche
 * pana o trece cineva, cu butonul, pe alegerea pe nivel. Contul de urgenta si
 * jurnalul sunt neschimbate.
 */
export function AdminSsoPanel({ page, onSaved }: { page: AuthenticationPage; onSaved(): void }) {
  const policy = page.policy
  const stored = policy?.ssoRequiredLevels ?? null
  const [levels, setLevels] = useState<number[] | null>(stored)
  const [breakGlass, setBreakGlass] = useState(policy?.breakGlassUserId ?? 0)
  const [busy, setBusy] = useState(false)
  const storedKey = stored === null ? 'legacy' : stored.join(',')

  useEffect(() => {
    setLevels(policy?.ssoRequiredLevels ?? null)
    setBreakGlass(policy?.breakGlassUserId ?? 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedKey, policy?.breakGlassUserId])

  if (!policy) return null

  const toggle = (level: number, on: boolean) =>
    setLevels((current) => {
      const next = (current ?? []).filter((l) => l !== level)
      return on ? [...next, level].sort((a, b) => a - b) : next
    })

  const save = async () => {
    setBusy(true)
    try {
      const answer =
        levels === null
          ? /* Regula veche: numai contul de urgenta se poate schimba aici. */
            await saveAdminSsoPolicy(policy.adminSsoRequired, breakGlass)
          : await saveSsoRequiredLevels(levels, breakGlass)
      toast(answer.message, answer.status === 'OK' ? 'Saved' : 'Error', answer.status === 'OK' ? 'success' : 'error')
      if (answer.status === 'OK') onSaved()
    } catch (error) {
      toast(error instanceof ApiError ? error.message : 'The setting could not be saved.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const legend = SSO_LEVELS.map((value) => ({ value, label: ACCESS_LEVEL_INFO[value]?.label ?? `#${value}` }))

  return (
    <div className="portlet light bordered">
      <div className="portlet-body">
        <h4 style={{ marginTop: 0 }}>
          Sign in only with single sign-on for these access levels{' '}
          <AccessLevelLegend what="Access levels" levels={legend} />
        </h4>
        {levels === null ? (
          <div data-testid="sso-legacy-rule">
            <p style={{ marginBottom: 4 }}>
              <strong>Legacy rule: administrators (by role/level)</strong> &mdash;{' '}
              {policy.adminSsoRequired ? 'on' : 'off'}
            </p>
            <span className="help-block">
              {policy.adminSsoRequired
                ? 'Accounts on the Service Provider or Super Admin (legacy) level, with the admin role, or with rights to manage accounts, roles or sign-in cannot use a local password.'
                : 'Nobody is required to use single sign-on.'}{' '}
              Choose the access levels instead to decide exactly who must use single sign-on.
            </span>
            {page.canWrite ? (
              <button
                className="btn btn-default btn-sm"
                type="button"
                style={{ marginBottom: 12 }}
                onClick={() => setLevels(legacyStartLevels(policy.adminSsoRequired))}
              >
                Choose per access level
              </button>
            ) : null}
          </div>
        ) : (
          <div data-testid="sso-levels">
            {SSO_LEVELS.map((level) => {
              const checked = levels.includes(level)
              const info = ACCESS_LEVEL_INFO[level]
              return (
                <div className="checkbox" style={{ marginTop: 0 }} key={level}>
                  <label>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!page.canWrite || (!policy.hasActiveProvider && !checked)}
                      onChange={(e) => toggle(level, e.target.checked)}
                    />{' '}
                    {info?.label ?? `#${level}`}
                  </label>
                  <span className="help-block" style={{ margin: '0 0 0 20px' }}>
                    {info?.short}
                    {ALSO[level] ? ` ${ALSO[level]}` : null}
                  </span>
                </div>
              )
            })}
            <span className="help-block">
              A person on a ticked level cannot sign in with a local password anywhere &mdash; portal, mobile app,
              Dailee or the IT Service Catalog; only the identity provider lets them in. Only the access level counts,
              not the role.
              {policy.hasActiveProvider ? null : ' Add and activate a provider first.'}
            </span>
            {stored === null ? (
              <span className="help-block" data-testid="sso-levels-unsaved">
                {policy.adminSsoRequired
                  ? 'Pre-ticked from the legacy rule: Super Admin and Service Provider. Accounts that were caught only by their role or rights are no longer covered unless their level is ticked.'
                  : 'Nothing is ticked, like the legacy rule (off).'}{' '}
                Nothing changes until you save.{' '}
                <button type="button" className="btn btn-link btn-xs" style={{ padding: 0 }} onClick={() => setLevels(null)}>
                  Keep the legacy rule
                </button>
              </span>
            ) : null}
          </div>
        )}
        <div className="form-group" style={{ maxWidth: 480 }}>
          <label className="control-label" htmlFor="authBreakGlass">
            Break-glass account
          </label>
          <select
            id="authBreakGlass"
            className="form-control"
            value={breakGlass}
            disabled={!page.canWrite}
            onChange={(e) => setBreakGlass(Number(e.target.value))}
          >
            <option value={0}>None</option>
            {policy.accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
          <span className="help-block" style={{ marginBottom: 0 }}>
            One local account that can still sign in with its password when the provider is down. Every time it does,
            it is recorded below.
          </span>
        </div>
        {page.canWrite ? (
          <button className="btn btn-info btn-sm" type="button" disabled={busy} onClick={() => void save()}>
            Save
          </button>
        ) : null}
      </div>
    </div>
  )
}
